import React, { useEffect, useState } from "react";
import { computeDropdownListHelper } from "./pagination.helper";
import SelectInput from "../select-input";

/**
 * All props are required because the default props have been set in the parent
 */
export interface PaginationPerPageProps {
  resultsCount: number;

  /*
   * When pageSize matches the dropdown option, it will be preselected
   */
  pageSize: number;
  resultsPerPageOptions: number[];
  resultsLabel: string;
  resultSingularLabel: string;
  resultsTitleLabel: string;
  onPageSizeChange: (number: string) => void;
}

export const ResultsPerPage = ({
  resultsPerPageOptions,
  pageSize,
  resultsCount,
  resultsLabel,
  resultsTitleLabel,
  resultSingularLabel,
  onPageSizeChange,
}: PaginationPerPageProps): React.ReactElement | null => {
  const [computedOptionsList, setComputedOptionsList] = useState<number[]>(
    computeDropdownListHelper(pageSize, resultsCount, resultsPerPageOptions)
  );
  const [value, setValue] = useState(pageSize);

  /*
   * Useful when results count changes and we need to show singular of result in dropdown. The dropdown will
   * only show "1 result" and other options shall not be shown.
   */
  useEffect(() => {
    setComputedOptionsList(
      computeDropdownListHelper(pageSize, resultsCount, resultsPerPageOptions)
    );
  }, [
    pageSize,
    resultSingularLabel,
    resultsCount,
    resultsLabel,
    resultsPerPageOptions,
  ]);

  /*
   * This is a possible error scenario where custom page size other than [10,25,50,100] is used
   * but the resultsPerPageOptions prop is not updated. Technically the pageSize should be one of
   * the options in the list, otherwise the component does not render.
   */
  if (!resultsPerPageOptions.includes(pageSize)) {
    console.warn(
      `The pageSize prop ${pageSize} is not one of the options in resultsPerPageOptions prop [${resultsPerPageOptions}], the dropdown wont be rendered.`
    );
    return null;
  }

  // This method recalculates the page items displayed and changes the text in results summary
  const onChangeHandler = (pageSize: string): void => {
    setValue(Number(pageSize));
    onPageSizeChange?.(pageSize);
  };

  const optionsList = computedOptionsList.map((item) => ({
    label: `${item} ${resultsLabel}`,
    value: `${item}`,
  }));

  /*
   * disabled prop comes handy when developer wants to show the page size but does not want to
   * implement logic for multiple page size scenarios.
   */
  return (
    <div className="spark-pagination__per-page">
      <SelectInput
        options={optionsList}
        onChange={(e, value): void => onChangeHandler(value)}
        value={`${value}`}
        ariaLabel={"Results Per Page"}
        title={resultsTitleLabel}
        defaultValue={undefined}
        disabled={undefined}
        errorMessage={undefined}
        id={undefined}
        label={undefined}
        name={undefined}
        onBlur={undefined}
        onFocus={undefined}
        required={undefined}
        status={undefined}
        statusMessage={undefined}
        testId={undefined}
      />
    </div>
  );
};
